import { formatNumber, formatCurrency, formatPercentage } from '../../utils/formatters';import React, { useState } from 'react';
import './RoiCalculator.css';
import logo from '../../assets/DTT2.png';

const AI_LICENSES = {
    chatWeb: {
        name: 'AI Agent License Chat Web',
        price: 119.95,
        tooltip: 'AI-powered chat functionality for your website, enabling intelligent customer interaction, support, and query resolution.'
    },
    voiceChatWeb: {
        name: 'AI Agent License Voice & Chat Web',
        price: 159.95,
        tooltip: 'Combines AI-driven voice and chat capabilities on your website for seamless customer engagement and enhanced user experiences.'
    },
    phoneInbound: {
        name: 'AI Agent Phone Voice Inbound',
        price: 224.95,
        tooltip: 'AI-enabled solution for managing incoming calls, with integrated web chat and voice support to streamline customer assistance.'
    },
    phoneOutbound: {
        name: 'AI Agent Phone Voice Outbound',
        price: 193.95,
        tooltip: 'AI-powered outbound call management, including integrated web chat and voice capabilities for follow-ups, sales, and reminders.'
    }
};

const STANDARD_ADDITIONS = {
    messenger: { name: 'Messenger AI Assistant', price: 89.95, tooltip: 'AI-powered chat functionality for your website, enabling intelligent customer interaction, support, and query resolution.' },
    discord: { name: 'Discord AI Assistant', price: 89.95, tooltip: 'AI-powered chat functionality for your Discord server, enabling intelligent customer interaction, support, and query resolution.' },
    email: { name: 'Email AI Assistant', price: 89.95, tooltip: 'AI-powered email functionality, enabling intelligent customer interaction, support, and query resolution.' },
    slack: { name: 'Slack AI Assistant', price: 89.95, tooltip: 'AI-powered chat functionality for your Slack workspace, enabling intelligent customer interaction, support, and query resolution.' },
    webForm: { name: 'Interactive Web Form', price: 89.95, tooltip: 'AI-powered web form functionality, enabling intelligent customer interaction, support, and query resolution.' },
    twitter: { name: 'Twitter AI Assistant', price: 89.95, tooltip: 'AI-powered chat functionality for your Twitter account, enabling intelligent customer interaction, support, and query resolution.' },
    knowledgeBase: { name: 'Knowledge Base', price: 89.95, tooltip: 'AI-powered knowledge base functionality, enabling intelligent customer interaction, support, and query resolution.' },
    linkedin: { name: 'LinkedIn AI Assistant', price: 89.95, tooltip: 'AI-powered chat functionality for your LinkedIn account, enabling intelligent customer interaction, support, and query resolution.' },
    dashboard: { name: 'Live Dashboard', price: 89.95, tooltip: 'AI-powered live dashboard functionality, enabling intelligent customer interaction, support, and query resolution.' },
    verification: { name: 'User Verification', price: 89.95, tooltip: 'AI-powered user verification functionality, enabling intelligent customer interaction, support, and query resolution.' }
};

const ADVANCED_ADDITIONS = {
    appointmentSetter: { 
        name: 'Appointment Setter', 
        price: 149.95,
        tooltip: 'AI-powered scheduling assistant that automates booking, sends reminders, and syncs seamlessly with calendars to optimise appointment management.'
    },
    interactiveCrm: { 
        name: 'Interactive CRM', 
        price: 149.95,
        tooltip: 'AI-integrated CRM system that enables dynamic customer interaction, automates updates, and provides actionable insights for enhanced relationship management.'
    },
    informationAnalyst: { 
        name: 'Information Analyst', 
        price: 149.95,
        tooltip: 'An AI assistant that processes and analyses data, delivering insights and reports to help make informed business decisions quickly and efficiently.'
    },
    whatsapp: { 
        name: 'WhatsApp AI Assistant', 
        price: 149.95,
        tooltip: 'AI assistant designed for WhatsApp to automate customer engagement, handle queries, and provide seamless support through real-time messaging.'
    },
    microsoftTeams: { 
        name: 'Microsoft Teams AI Assistant', 
        price: 149.95,
        tooltip: 'AI assistant for Microsoft Teams to streamline collaboration, automate responses, and assist with meeting management and team communication.'
    },
    sentimentAnalysis: { 
        name: 'Sentiment Analysis Assistant', 
        price: 149.95,
        tooltip: 'AI-driven tool that analyses customer sentiment during interactions to provide real-time insights for personalised responses and service improvement.'
    }
};

const EMPLOYEE_COSTS = {
    nics: { name: 'Employer NICs', percentage: 13.8 },
    pension: { name: 'Pension Contributions', percentage: 4 },
    equipment: { name: 'Equipment and Tools', percentage: 2 },
    health: { name: 'Health and Insurance', percentage: 4 },
    office: { name: 'Office/Remote Working Costs', percentage: 3 },
    travel: { name: 'Travel and Transport', percentage: 4 },
    training: { name: 'Training and Development', percentage: 1.8 },
    perks: { name: 'Employee Perks', percentage: 1.5 },
    misc: { name: 'Miscellaneous Allowances', percentage: 2.5 }
};

const RoiCalculator = () => {
    const [formData, setFormData] = useState({
        grossSalary: '4000',
        workingHours: 8,
        contractDuration: 12,
        selectedLicense: 'chatWeb',  
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        website: ''  
    });

    const [selectedAdditions, setSelectedAdditions] = useState({
        // Standard Additions
        messenger: false,
        discord: false,
        email: false,
        slack: false,
        webForm: false,
        twitter: false,
        knowledgeBase: false,
        linkedin: false,
        dashboard: false,
        verification: false,
        // Advanced Additions
        appointmentSetter: false,
        interactiveCrm: false,
        informationAnalyst: false,
        whatsapp: false,
        microsoftTeams: false,
        sentimentAnalysis: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setSelectedAdditions(prevState => ({
            ...prevState,
            [name]: checked
        }));
    };

    const calculateDiscount = (price) => {
        const maxDiscount = 0.45;
        const maxDuration = 60;
        const minDuration = 1;
        const discountPercentage = ((formData.contractDuration - minDuration) / (maxDuration - minDuration)) * maxDiscount;
        return {
            original: price,
            discounted: price * (1 - discountPercentage)
        };
    };

    const formatPrice = (price) => `£${price.toFixed(2)}`;

    const calculateTotalCost = () => {
        // Calculate base total without discount
        let baseTotal = formData.selectedLicense ? AI_LICENSES[formData.selectedLicense].price : 0;

        // Add standard additions
        Object.entries(selectedAdditions).forEach(([key, value]) => {
            if (value) {
                if (STANDARD_ADDITIONS[key]) {
                    baseTotal += STANDARD_ADDITIONS[key].price;
                } else if (ADVANCED_ADDITIONS[key]) {
                    baseTotal += ADVANCED_ADDITIONS[key].price;
                }
            }
        });

        const { original, discounted } = calculateDiscount(baseTotal);
        return {
            baseTotal: original,
            discountedTotal: discounted,
            discountPercentage: ((original - discounted) / original) * 100
        };
    };

    const calculateHumanCosts = (grossSalary, hours = 8) => {
        const salary = parseFloat(grossSalary) || 0;
        const hourlyScaling = hours / 8; 
        const scaledSalary = salary * hourlyScaling;
        
        const costs = {
            base: scaledSalary,
            details: {
                baseSalary: {
                    name: 'Gross Monthly Salary',
                    percentage: formatNumber(hourlyScaling * 100),
                    amount: formatNumber(scaledSalary)
                }
            },
            hoursMultiplier: hourlyScaling
        };
    
        let total = scaledSalary;
        Object.entries(EMPLOYEE_COSTS).forEach(([key, { name, percentage }]) => {
            const amount = (scaledSalary * percentage) / 100;
            total += amount;
            costs.details[key] = {
                name,
                percentage: formatNumber(percentage), 
                amount: formatNumber(amount)
            };
        });
    
        costs.total = total;
        return costs;
    };

    const calculateROI = () => {
        const humanCosts = calculateHumanCosts(formData.grossSalary, formData.workingHours);
        const aiCosts = calculateTotalCost();
        const savings = humanCosts.total - aiCosts.discountedTotal;
        const roi = aiCosts.discountedTotal > 0 ? ((savings / aiCosts.discountedTotal) * 100) : 0;
    
        return {
            humanCost: formatNumber(humanCosts.total),
            humanCostDetails: humanCosts.details,
            hoursMultiplier: humanCosts.hoursMultiplier,
            aiCost: formatNumber(aiCosts.discountedTotal),
            savings: formatNumber(savings),
            roi: formatPercentage(roi),
            discountPercentage: formatPercentage(aiCosts.discountPercentage)
        };
    };

    const formatWebhookData = () => {
        const selectedLicenseDetails = AI_LICENSES[formData.selectedLicense];
        const { original: originalPrice, discounted: discountedPrice } = calculateDiscount(selectedLicenseDetails.price);
        const discountPercentage = ((originalPrice - discountedPrice) / originalPrice) * 100;

        // Calculate discounted prices for additions
        const standardDiscountedPrice = calculateDiscount(89.95).discounted;
        const advancedDiscountedPrice = calculateDiscount(149.95).discounted;

        return {
            userDetails: {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                company: formData.company,
                website: formData.website
            },
            licenseSelection: {
                agentLicenseChatWeb: {
                    selected: formData.selectedLicense === 'chatWeb',
                    price: formData.selectedLicense === 'chatWeb' ? formatNumber(discountedPrice) : 0
                },
                agentLicenseVoiceChatWeb: {
                    selected: formData.selectedLicense === 'voiceChatWeb',
                    price: formData.selectedLicense === 'voiceChatWeb' ? formatNumber(discountedPrice) : 0
                },
                agentPhoneVoiceInbound: {
                    selected: formData.selectedLicense === 'phoneInbound',
                    price: formData.selectedLicense === 'phoneInbound' ? formatNumber(discountedPrice) : 0
                },
                agentPhoneVoiceOutbound: {
                    selected: formData.selectedLicense === 'phoneOutbound',
                    price: formData.selectedLicense === 'phoneOutbound' ? formatNumber(discountedPrice) : 0
                }
            },
            contractTerms: {
                duration: formData.contractDuration,
                discountApplied: formatNumber(discountPercentage)
            },
            standardAdditions: {
                messengerAiAssistant: {
                    selected: selectedAdditions.messenger,
                    price: selectedAdditions.messenger ? formatNumber(standardDiscountedPrice) : 0
                },
                discordAiAssistant: {
                    selected: selectedAdditions.discord,
                    price: selectedAdditions.discord ? formatNumber(standardDiscountedPrice) : 0
                },
                emailAiAssistant: {
                    selected: selectedAdditions.email,
                    price: selectedAdditions.email ? formatNumber(standardDiscountedPrice) : 0
                },
                slackAiAssistant: {
                    selected: selectedAdditions.slack,
                    price: selectedAdditions.slack ? formatNumber(standardDiscountedPrice) : 0
                },
                interactiveWebForm: {
                    selected: selectedAdditions.webForm,
                    price: selectedAdditions.webForm ? formatNumber(standardDiscountedPrice) : 0
                },
                twitterAiAssistant: {
                    selected: selectedAdditions.twitter,
                    price: selectedAdditions.twitter ? formatNumber(standardDiscountedPrice) : 0
                },
                knowledgeBase: {
                    selected: selectedAdditions.knowledgeBase,
                    price: selectedAdditions.knowledgeBase ? formatNumber(standardDiscountedPrice) : 0
                },
                linkedinAiAssistant: {
                    selected: selectedAdditions.linkedin,
                    price: selectedAdditions.linkedin ? formatNumber(standardDiscountedPrice) : 0
                },
                liveDashboard: {
                    selected: selectedAdditions.dashboard,
                    price: selectedAdditions.dashboard ? formatNumber(standardDiscountedPrice) : 0
                },
                userVerification: {
                    selected: selectedAdditions.verification,
                    price: selectedAdditions.verification ? formatNumber(standardDiscountedPrice) : 0
                },
                costPerAddition: formatNumber(standardDiscountedPrice),
                totalCost: formatNumber(Object.keys(selectedAdditions)
                    .filter(key => STANDARD_ADDITIONS[key] && selectedAdditions[key])
                    .length * standardDiscountedPrice)
            },
            advancedAdditions: {
                appointmentSetter: {
                    selected: selectedAdditions.appointmentSetter,
                    price: selectedAdditions.appointmentSetter ? formatNumber(advancedDiscountedPrice) : 0
                },
                interactiveCrm: {
                    selected: selectedAdditions.interactiveCrm,
                    price: selectedAdditions.interactiveCrm ? formatNumber(advancedDiscountedPrice) : 0
                },
                informationAnalyst: {
                    selected: selectedAdditions.informationAnalyst,
                    price: selectedAdditions.informationAnalyst ? formatNumber(advancedDiscountedPrice) : 0
                },
                whatsappAiAssistant: {
                    selected: selectedAdditions.whatsapp,
                    price: selectedAdditions.whatsapp ? formatNumber(advancedDiscountedPrice) : 0
                },
                microsoftTeamsAiAssistant: {
                    selected: selectedAdditions.microsoftTeams,
                    price: selectedAdditions.microsoftTeams ? formatNumber(advancedDiscountedPrice) : 0
                },
                sentimentAnalysisAssistant: {
                    selected: selectedAdditions.sentimentAnalysis,
                    price: selectedAdditions.sentimentAnalysis ? formatNumber(advancedDiscountedPrice) : 0
                },
                costPerAddition: formatNumber(advancedDiscountedPrice),
                totalCost: formatNumber(Object.keys(selectedAdditions)
                    .filter(key => ADVANCED_ADDITIONS[key] && selectedAdditions[key])
                    .length * advancedDiscountedPrice)
            },
            employeeCosts: {
                grossMonthlySalary: formatNumber(parseFloat(formData.grossSalary) || 0),
                workingHoursPerDay: formData.workingHours,
                aiProductivityPercentage: formatNumber((formData.workingHours / 24) * 100)
            },
            costSummary: {
                totalMonthlyAiCost: formatNumber(calculateTotalCost().discountedTotal),
                totalMonthlyEmployeeCost: formatNumber(calculateHumanCosts(formData.grossSalary, formData.workingHours).total),
                monthlySavings: formatNumber(calculateHumanCosts(formData.grossSalary, formData.workingHours).total - calculateTotalCost().discountedTotal),
                annualSavings: formatNumber((calculateHumanCosts(formData.grossSalary, formData.workingHours).total - calculateTotalCost().discountedTotal) * 12)
            },
            metadata: {
                submissionDate: new Date().toISOString(),
                calculatorVersion: "1.1"
            }
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const webhookData = formatWebhookData();
            const response = await fetch('https://hook.eu2.make.com/k6y6fwxl85kd9q8ziutr24mr6bsxkpfq', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(webhookData)
            });

            if (!response.ok) {
                throw new Error('Submission failed');
            }

            setSubmitStatus('success');
            // Optional: Reset form or show success message
        } catch (error) {
            console.error('Submission error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const updateSliderProgress = (e) => {
        const target = e.target;
        const min = target.min;
        const max = target.max;
        const val = target.value;
        const percentage = (val - min) * 100 / (max - min);
        target.style.setProperty('--slider-progress', `${percentage}%`);
    };

    const results = calculateROI();

    return (
        <div className="calculator-container">
            <img src={logo} alt="Designed To Talk" className="logo" />
            <h1 className="calculator-title">Business Value ROI Calculator</h1>
            <h2 className="section-title">Employee vs AI Assistant Costs</h2>
            <p className="description-text">Explore how AI assistants can augment your team, help you reduce costs, maintain or even increase productivity. Whether you're considering hiring additional staff or facing tough decisions about letting staff go. Our AI assistant can also be part of our intelligent unified phone and contact center solution.</p>
            
            <div className="calculator-grid">
                <div className="section">
                    <h2 className="section-title">Employee Costs</h2>
                    <div className="form-group">
                        <label htmlFor="grossSalary">Gross Monthly Salary (£)</label>
                        <input
                            type="number"
                            id="grossSalary"
                            name="grossSalary"
                            value={formData.grossSalary}
                            onChange={handleInputChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Working Hours per Day</label>
                        <div className="slider-group">
                            <input
                                type="range"
                                min="1"
                                max="24"
                                name="workingHours"
                                value={formData.workingHours}
                                onChange={(e) => {
                                    updateSliderProgress(e);
                                    setFormData(prevState => ({ ...prevState, workingHours: parseInt(e.target.value) }));
                                }}
                                onInput={updateSliderProgress}
                                style={{"--slider-progress": `${((formData.workingHours - 1) * 100 / 23)}%`}}
                            />
                            <div className="slider-labels">
                                <span>
                                    <span className="number-value">{formData.workingHours}</span>
                                    <span className="label-text"> hours</span>
                                </span>
                                <span>
                                    <span className="number-value">{((formData.workingHours / 24) * 100).toFixed(0)}%</span>
                                    <span className="label-text"> of AI agent productivity</span>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="cost-breakdown">
                        <h3 className="section-title">Additional Costs Breakdown</h3>
                        <div className="cost-details">
                            {Object.entries(results.humanCostDetails || {}).map(([key, { name, percentage, amount }]) => (
                                <div key={key} className="cost-item">
                                    <div className="cost-label">
                                        <span>{name}</span>
                                        <span className="percentage">{percentage}%</span>
                                    </div>
                                    <span className="amount">£{amount}</span>
                                </div>
                            ))}
                        </div>
                        <div className="video-container">
                            <div style={{padding:'56.25% 0 0 0', position:'relative'}}>
                                <iframe 
                                    src="https://player.vimeo.com/video/1033422687?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479" 
                                    frameBorder="0" 
                                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write" 
                                    style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}} 
                                    title="IT service AI Demo">
                                </iframe>
                            </div>
                            <script src="https://player.vimeo.com/api/player.js"></script>
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2 className="section-title">AI Assistant Costs</h2>
                    <div className="cost-section">
                        <h3 className="section-title">License Type</h3>
                        <div className="license-options">
                            {Object.entries(AI_LICENSES).map(([key, { name, price, tooltip }]) => {
                                const { original, discounted } = calculateDiscount(price);
                                return (
                                    <div key={key} className="license-option">
                                        <input
                                            type="radio"
                                            id={key}
                                            name="selectedLicense"
                                            value={key}
                                            checked={formData.selectedLicense === key}
                                            onChange={handleInputChange}
                                        />
                                        <label htmlFor={key}>
                                            <div className="license-info">
                                                <div className="license-name">
                                                    {name}
                                                    <span className="info-icon">ⓘ</span>
                                                    <div className="solid-tooltip">
                                                        <div className="solid-tooltip-inner">{tooltip}</div>
                                                    </div>
                                                </div>
                                                <div className="license-price">
                                                    <span className="original-price">{formatPrice(original)}</span>
                                                    <span className="discounted-price">{formatPrice(discounted)}</span>
                                                </div>
                                            </div>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="cost-section">
                        <h3 className="section-title">Contract Terms</h3>
                        <div className="slider-group">
                            <label htmlFor="contractDuration">Contract Duration (months)</label>
                            <input
                                type="range"
                                min="1"
                                max="36"
                                name="contractDuration"
                                value={formData.contractDuration}
                                onChange={(e) => {
                                    updateSliderProgress(e);
                                    setFormData(prevState => ({ ...prevState, contractDuration: parseInt(e.target.value) }));
                                }}
                                onInput={updateSliderProgress}
                                style={{"--slider-progress": `${((formData.contractDuration - 1) * 100 / 35)}%`}}
                            />
                            <div className="duration-info">
                                <span>{formData.contractDuration} months</span>
                                <span className="discount-info">
                                    {formData.contractDuration === 1 ? '0' : ((formData.contractDuration - 1) / 35 * 45).toFixed(1)}% discount applied
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="cost-section">
                        <div className="section-header">
                            <h3 className="section-title">Standard AI Additions</h3>
                            <div className="price-info">
                                <span className="original-cost">{formatPrice(89.95)}</span>
                                <span className="discounted-cost">{formatPrice(calculateDiscount(89.95).discounted)} each</span>
                            </div>
                        </div>
                        <div className="checkbox-list">
                            {Object.entries(STANDARD_ADDITIONS).map(([key, { name, price, tooltip }]) => (
                                <div key={key} className="checkbox-item">
                                    <input
                                        type="checkbox"
                                        id={key}
                                        name={key}
                                        checked={selectedAdditions[key]}
                                        onChange={handleCheckboxChange}
                                    />
                                    <label htmlFor={key}>
                                        <div className="license-name">
                                            {name}
                                            <span className="info-icon">ⓘ</span>
                                            <div className="solid-tooltip">
                                                <div className="solid-tooltip-inner">{tooltip}</div>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="cost-section">
                        <div className="section-header">
                            <h3 className="section-title">Advanced AI Additions</h3>
                            <div className="price-info">
                                <span className="original-cost">{formatPrice(149.95)}</span>
                                <span className="discounted-cost">{formatPrice(calculateDiscount(149.95).discounted)} each</span>
                            </div>
                        </div>
                        <div className="checkbox-list advanced-additions">
                            {Object.entries(ADVANCED_ADDITIONS).map(([key, { name, price, tooltip }]) => (
                                <div key={key} className="checkbox-item">
                                    <input
                                        type="checkbox"
                                        id={key}
                                        name={key}
                                        checked={selectedAdditions[key]}
                                        onChange={handleCheckboxChange}
                                    />
                                    <label htmlFor={key}>
                                        <div className="license-name">
                                            {name}
                                            <span className="info-icon">ⓘ</span>
                                            <div className="solid-tooltip">
                                                <div className="solid-tooltip-inner">{tooltip}</div>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="bottom-grid">
                <div className="results">
                    <h2 className="section-title">Monthly Cost Comparison</h2>
                    <div className="result-item">
                        <span>Employee Cost:</span>
                        <span>£{results.humanCost}</span>
                    </div>
                    <div className="result-item">
                        <span>AI Assistant Cost:</span>
                        <span>£{results.aiCost}</span>
                    </div>
                    <div className="result-item">
                        <span>Monthly Savings:</span>
                        <span>£{results.savings}</span>
                    </div>
                    <div className="result-item">
                        <span>ROI:</span>
                        <span>{results.roi}</span>
                    </div>
                    <div className="result-item">
                        <span>Annual Savings:</span>
                        <span>£{formatNumber(parseFloat(results.savings.replace(/[£,]/g, '')) * 12)}</span>
                    </div>
                </div>

                <div className="section contact-form">
                    <h2 className="section-title">Request Information</h2>
                    <form onSubmit={handleSubmit}>
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="firstName">First Name</label>
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="lastName">Last Name</label>
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="phone">Phone Number</label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="company">Company Name</label>
                                <input
                                    type="text"
                                    id="company"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="website">Website</label>
                                <input
                                    type="url"
                                    id="website"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                        <button 
                            type="submit" 
                            className="submit-button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                        {submitStatus === 'success' && (
                            <div className="success-message">
                                Thank you for your submission! We'll be in touch shortly.
                            </div>
                        )}
                        {submitStatus === 'error' && (
                            <div className="error-message">
                                Sorry, there was an error submitting your request. Please try again.
                            </div>
                        )}
                    </form>
                    <a 
                        href="https://www.designedtotalk.com/ai-business-world" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="ai-world-button"
                    >
                        Go to AI World
                    </a>
                </div>
            </div>
            <p className="terms-note">Please note: Additional AI Configuration cost to customise the solution to your business and ongoing AI usage charges apply. Please ask for a formal proposal from our team - <a href="mailto:team@designedtotalk.com">team@designedtotalk.com</a>. Our standard terms and conditions apply; visit <a href="https://www.designedtotalk.com/terms" target="_blank" rel="noopener noreferrer">www.designedtotalk.com/terms</a> for details.</p>
        </div>
    );
};

export default RoiCalculator;
